// A seller's own amounts (asking price, minimum bid, price when new) are exact. The seller's price
// forms show and save them as they are, never rounded or cut to whole amounts (WHO-5991 decision 14,
// WHO-6302). The rules are the apps' (`SellerPriceField.kt`, WHO-6211).

/** An amount a seller price field reads: whole part, then optionally a separator and up to two decimals. */
const PRICE_TEXT = /^(\d+)(?:[.,](\d{0,2}))?$/;

/** A euro sign typed or pasted around the amount (`€ 302,50`, `302,50 €`), which the field already shows. */
const EURO_SIGN = /^€\s*|\s*€$/g;

/** The lowest minimum bid, as a percentage of the asking price. */
const MINIMUM_BID_PERCENT_OF_ASKING_PRICE = 10;

/** "Can't be less than {{0}}": a minimum bid under the floor, naming the floor (the apps' `CantBeLessThen`). */
export const MINIMUM_BID_BELOW_FLOOR_ERROR =
  "common:common-error-price-less-than";

/** "Can't be more than {{0}}": a minimum bid at or above the asking price, naming it (`CantBeMoreThen`). */
export const MINIMUM_BID_NOT_BELOW_ASKING_ERROR =
  "common:common-error-price-more-than";

/** An amount in whole cents. It drops floating-point noise: 20.1 × 100 is 2010.0000000000002. */
export const toCents = (amount: number) => Math.round(amount * 100);

/**
 * A seller price field's text as an amount, or `null` when it holds no valid one. Either decimal
 * separator is accepted (Dutch writes `302,50`); `302,` reads as 302. Anything else is refused,
 * never guessed: a Dutch thousands dot (`25.000`, `1.500,00`) or a third decimal reads as no
 * amount, as the buyer's offer field does with `1.234` (WHO-6292), so the form says "Invalid"
 * instead of saving € 25 for 25 thousand.
 */
export const toPriceAmount = (text: string): number | null => {
  const match = PRICE_TEXT.exec(text.trim().replace(EURO_SIGN, ""));

  if (!match) {
    return null;
  }

  const [, whole, fraction = ""] = match;
  const cents = toCents(Number(`${whole}.${fraction || "0"}`));

  return Number.isSafeInteger(cents) ? cents / 100 : null;
};

/**
 * An amount as the text of a seller price field: exact, with cents only when there are cents
 * (302.5 → `302.50`, 302 → `302`).
 */
export const toPriceFieldText = (amount: number) => {
  const cents = toCents(amount);
  const sign = cents < 0 ? "-" : "";
  const whole = Math.floor(Math.abs(cents) / 100);
  const fraction = Math.abs(cents) % 100;

  return fraction === 0
    ? `${sign}${whole}`
    : `${sign}${whole}.${String(fraction).padStart(2, "0")}`;
};

/**
 * The lowest minimum bid a seller may set, in cents: 10% of the asking price, rounded up to the
 * cent. An asking price with cents makes the percentage land on a third decimal (€ 302,51 → 30,251),
 * and a float compare rejects an exact 10% (0.1 × 20.1 is 2.0100000000000002).
 */
export const minimumBidFloorCents = (askingPrice: number) =>
  Math.ceil((toCents(askingPrice) * MINIMUM_BID_PERCENT_OF_ASKING_PRICE) / 100);

/** The lowest minimum bid a seller may set for `askingPrice`, which the error names (as the apps). */
export const minimumBidFloor = (askingPrice: number) =>
  minimumBidFloorCents(askingPrice) / 100;

/** Whether `minimumBid` is below the lowest minimum bid for `askingPrice`. */
export const isBelowMinimumBidFloor = (
  minimumBid: number,
  askingPrice: number
) => toCents(minimumBid) < minimumBidFloorCents(askingPrice);

/**
 * What's wrong with `minimumBid` for `askingPrice`, as the error's key, or `null` when nothing is:
 * a minimum bid is at least 10% of the asking price and below it, both in whole cents (the apps'
 * `validateAsBidPrice`; decided for the web on 21 Oct, WHO-6302).
 */
export const minimumBidError = (minimumBid: number, askingPrice: number) => {
  if (isBelowMinimumBidFloor(minimumBid, askingPrice)) {
    return MINIMUM_BID_BELOW_FLOOR_ERROR;
  }

  if (toCents(minimumBid) >= toCents(askingPrice)) {
    return MINIMUM_BID_NOT_BELOW_ASKING_ERROR;
  }

  return null;
};

/**
 * The amount a minimum-bid error names (`{{0}}`), formatted: the asking price when the minimum
 * isn't below it, otherwise the floor. Nothing while the asking price reads no amount.
 */
export const minimumBidErrorValue = (
  message: string | undefined,
  askingPrice: number | null | undefined,
  formatPrice: (amount: number) => string
) => {
  if (askingPrice == null || Number.isNaN(askingPrice)) {
    return undefined;
  }

  return {
    0: formatPrice(
      message === MINIMUM_BID_NOT_BELOW_ASKING_ERROR
        ? askingPrice
        : minimumBidFloor(askingPrice)
    ),
  };
};
